import { type useLazyQuery } from '@apollo/client/react';
import { type Dispatch, type SetStateAction, useContext, useState, type FunctionComponent, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import {
	type GetProductConfigurationQuery,
	type Exact,
	type GetProductConfigurationInput
} from '../../../../__generated__/graphql-client-types';
import { AddToCartContext } from '../../../../contexts/add-to-cart/add-to-cart.context';
import { ProductStickyNavContext } from '../../../../contexts/product-sticky-nav/product-sticky-nav.context';
import { VariantContext } from '../../../../contexts/variant/variant.context';
import { ANALYTICS_PAGE_NAMES } from '../../../../helpers/analytics/analytics.helper';
import { ADD_TO_CART_BUTTON_TYPE } from '../../../../helpers/product-helper/add-to-cart-button/add-to-cart-button.helper';
import { isConfigurationValid } from '../../../../helpers/product-helper/configuration/product-configuration.helper';
import { canAddToCart } from '../../../../helpers/product-helper/product.helper';
import { useAddToCartButton } from '../../../../hooks/add-to-cart/add-to-cart-button.hooks';
import { useRouterLocation } from '../../../../hooks/routing/location.hook';
import { GreyBox } from '../../../common-components/grey-box/grey-box.component';
import { QuantityInput } from '../../../inputs/quantity-input/quantity-input.component';
import { AddToCartButtonFactory } from '../../add-to-cart-button/add-to-cart-button.component';
import { SqFtAddToCart } from '../../sqft-add-to-cart/sqft-add-to-cart.component';
import { SqFtCalculator } from '../../sqft-calculator/sqft-calculator.component';
import { cartQuantitySelector } from './add-to-cart-section.css';
export type AddToCartSectionProps = {
	onAddToCartClick: (addToCartButtonType?: ADD_TO_CART_BUTTON_TYPE) => void;
	units: {
		unitsValue: number;
		setUnits: Dispatch<SetStateAction<number>>;
	};
	status: useLazyQuery.Result<GetProductConfigurationQuery, Exact<{ input: GetProductConfigurationInput }>>;
	isAddingToCart?: boolean;
};

export const AddToCartSection: FunctionComponent<AddToCartSectionProps> = ({ onAddToCartClick, units, status, isAddingToCart }) => {
	const { selectedVariant } = useContext(VariantContext);
	const { selection, setCartQuantity } = useContext(AddToCartContext);
	const { setShouldShowATC } = useContext(ProductStickyNavContext);
	const { ref: atcRef, entry: atcEntry } = useInView();
	const { unitsValue, setUnits } = units;

	const { search } = useRouterLocation();
	const searchParams = new URLSearchParams(search);
	const quantityParam = searchParams.get('quantity');

	const [inputQuantity, setInputQuantity] = useState<number | undefined>(Number(quantityParam) || 1);
	const configurationValid = selection ? isConfigurationValid(selection) : Boolean(selection);

	const { buttonType, addToCartActionStatus } = useAddToCartButton({
		pageName: ANALYTICS_PAGE_NAMES.PRODUCT_DETAILS_PAGE
	});

	const isSettled = status.dataState === 'complete';

	// allows quantity input to be set to undefined when the input is empty
	const handleInputQuantityChange = (newQuanity?: number) => {
		setInputQuantity(newQuanity);
		if (newQuanity !== undefined) {
			setCartQuantity(newQuanity);
		}
	};

	useEffect(() => {
		if (atcEntry) {
			// calculation to check if the viewport is below the main ATC button
			// using only the inView boolean, the sticky button will render on mount if product has rec ops
			const viewportBelowMainButton = atcEntry.boundingClientRect.y < -atcEntry.boundingClientRect.height;
			if (viewportBelowMainButton) {
				setShouldShowATC(true);
			} else {
				setShouldShowATC(false);
			}
		}
	}, [atcEntry]);

	return (
		<div className={'nl2 nr2 ml0-ns mr0-ns'} ref={atcRef}>
			{canAddToCart(selectedVariant) && (
				<>
					<GreyBox className={'flex items-center mt3 ml0-l mb2 ba b--theme-grey-light pa2-ns'}>
						<div className={`${cartQuantitySelector} pr1 pr0-l mr3-l`}>
							<QuantityInput
								quantity={inputQuantity}
								setQuantity={handleInputQuantityChange}
								automationHook="cart-quantity"
								isSettled={isSettled}
							/>
						</div>
						<div className="pl1 pl0-l pa0-l w-70 flex-auto-ns">
							<div className="w-100">
								<AddToCartButtonFactory
									buttonType={buttonType}
									addToCartButtonType={ADD_TO_CART_BUTTON_TYPE.DEFAULT}
									handleOnClick={() => onAddToCartClick(ADD_TO_CART_BUTTON_TYPE.DEFAULT)}
									disabled={!configurationValid || !inputQuantity}
									status={addToCartActionStatus}
									isBusy={isAddingToCart}
								/>
							</div>
						</div>
					</GreyBox>
					{!inputQuantity && isSettled && (
						<div className="f7 theme-error pl2-ns pl3">Please enter a quantity greater than 0.</div>
					)}
				</>
			)}
			{selectedVariant?.priceInfo.packaging && (
				<div className={`flex flex-column`}>
					<SqFtAddToCart
						className={`mt3`}
						priceInfo={selectedVariant.priceInfo}
						units={unitsValue}
						pageName="Product-PLA"
						onClick={onAddToCartClick}
						setQuantity={setCartQuantity}
					/>
					<SqFtCalculator className={`mt3`} onChange={setUnits} />
				</div>
			)}
		</div>
	);
};
